import React, { useState } from 'react';
import { Task } from '../types';
import { List, Plus, ChevronRight, Check, Clock, User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Progress } from './ui/progress';
import { useNavigate } from 'react-router';

interface SubtasksProps {
  task: Task;
  allTasks: Task[];
  users: any[];
  onCreateSubtask?: (title: string) => Promise<void>;
}

export default function Subtasks({ task, allTasks, users, onCreateSubtask }: SubtasksProps) {
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');

  // Get subtasks
  const subtasks = task.subtaskIds
    .map((id) => allTasks.find((t) => t.id === id))
    .filter(Boolean) as Task[];

  // Calculate progress
  const completedSubtasks = subtasks.filter(
    (st) => st.status === 'Done' || st.status === 'Completed'
  ).length;
  const progressPercent = subtasks.length > 0 ? (completedSubtasks / subtasks.length) * 100 : 0;

  // Calculate total hours
  const totalEstimated = subtasks.reduce((sum, st) => sum + (st.estimatedHours || 0), 0);
  const totalLogged = subtasks.reduce(
    (sum, st) => sum + (st.timeLogs?.reduce((s, log) => s + log.hours, 0) || 0),
    0
  );

  const handleCreateSubtask = async () => {
    if (!newSubtaskTitle.trim() || !onCreateSubtask) return;

    try {
      await onCreateSubtask(newSubtaskTitle);
      setNewSubtaskTitle('');
      setIsCreating(false);
    } catch (error) {
      console.error('Failed to create subtask:', error);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <List className="w-5 h-5" />
            Subtasks
            {subtasks.length > 0 && (
              <Badge variant="outline">
                {completedSubtasks}/{subtasks.length}
              </Badge>
            )}
          </span>
          {onCreateSubtask && !isCreating && (
            <Button size="sm" variant="outline" onClick={() => setIsCreating(true)}>
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress bar */}
        {subtasks.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Overall Progress</span>
              <span className="font-medium">{progressPercent.toFixed(0)}%</span>
            </div>
            <Progress value={progressPercent} className="h-2" />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {totalLogged.toFixed(1)}h logged / {totalEstimated}h estimated
              </span>
            </div>
          </div>
        )}

        {/* Create new subtask */}
        {isCreating && (
          <div className="flex gap-2 p-3 bg-primary/10 rounded-lg border border-primary/40">
            <Input
              placeholder="Subtask title..."
              value={newSubtaskTitle}
              onChange={(e) => setNewSubtaskTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCreateSubtask();
                } else if (e.key === 'Escape') {
                  setIsCreating(false);
                  setNewSubtaskTitle('');
                }
              }}
              autoFocus
              className="flex-1"
            />
            <Button size="sm" onClick={handleCreateSubtask} disabled={!newSubtaskTitle.trim()}>
              Create
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setIsCreating(false);
                setNewSubtaskTitle('');
              }}
            >
              Cancel
            </Button>
          </div>
        )}

        {/* Subtasks list */}
        {subtasks.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <List className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No subtasks yet</p>
            {onCreateSubtask && (
              <Button
                size="sm"
                variant="link"
                onClick={() => setIsCreating(true)}
                className="mt-2"
              >
                Create your first subtask
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {subtasks.map((subtask) => (
              <SubtaskCard
                key={subtask.id}
                task={subtask}
                users={users}
                onClick={() => navigate(`/task/${subtask.id}`)}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface SubtaskCardProps {
  task: Task;
  users: any[];
  onClick: () => void;
}

function SubtaskCard({ task, users, onClick }: SubtaskCardProps) {
  const isComplete = task.status === 'Done' || task.status === 'Completed';
  const assignees = users.filter((u) => task.assigneeIds.includes(u.id));
  const totalLogged = task.timeLogs?.reduce((sum, log) => sum + log.hours, 0) || 0;
  const timeProgress =
    task.estimatedHours && task.estimatedHours > 0
      ? (totalLogged / task.estimatedHours) * 100
      : 0;

  return (
    <div
      onClick={onClick}
      className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
        isComplete
          ? 'bg-chart-2/10 border-chart-2/40'
          : 'bg-white bg-muted/40 border-border hover:border-primary/40'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox indicator */}
        <div
          className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
            isComplete
              ? 'bg-chart-2/10 border-chart-2/40'
              : 'border-border'
          }`}
        >
          {isComplete && <Check className="w-3 h-3 text-white" />}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h5
              className={`text-sm font-medium ${
                isComplete
                  ? 'line-through text-muted-foreground'
                  : 'text-muted-foreground'
              }`}
            >
              {task.title}
            </h5>
            <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          </div>

          <div className="flex items-center gap-2 flex-wrap text-xs">
            <Badge variant="outline" className="font-mono">
              {task.id}
            </Badge>
            <Badge className="text-xs">{task.status}</Badge>
            {task.priority && (
              <span className="text-muted-foreground">{task.priority}</span>
            )}
          </div>

          {/* Progress and assignees */}
          <div className="flex items-center justify-between mt-2 gap-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {task.estimatedHours && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {totalLogged.toFixed(1)}/{task.estimatedHours}h
                </span>
              )}
              {assignees.length > 0 && (
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {assignees[0].name}
                  {assignees.length > 1 && ` +${assignees.length - 1}`}
                </span>
              )}
            </div>
            {task.estimatedHours && task.estimatedHours > 0 && (
              <div className="flex items-center gap-1">
                <Progress
                  value={Math.min(timeProgress, 100)}
                  className={
                    timeProgress > 100
                      ? 'h-1.5 w-16 [&::-webkit-progress-value]:bg-destructive/10 [&::-moz-progress-bar]:bg-destructive/10'
                      : 'h-1.5 w-16'
                  }
                />
                <span className="text-xs text-muted-foreground w-8 text-right">
                  {timeProgress.toFixed(0)}%
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
